/* global satismeter */
/// <reference types="gapi" />
/// <reference types="gapi.auth2" />
import React, { Component } from "react";
import { ConnectedRouter } from "connected-react-router";
import Routes from "./routes";
import withRedux from "../withRedux";
import { loadConversations } from "../actions/chat";
import { logout, getPaymentCards, loadAdverts, refreshMyProfile, loadFriends } from "../actions";
import UserContext from "../contexts/userContext";
import loadScript from "../libs/loadScript";
import { getAccessToken, hasAuthCookies, removeTokens } from "../libs/tokens";
import bodyClassSelector from "../selectors/bodyClassSelector";
import LayoutContext, { LayoutContextProps } from "../contexts/layoutContext";
import ChatSocketContext from "../contexts/chatSocketContext";
import { History } from "history";
import { Dispatch } from "redux";
import ChatSocketManager from "../libs/chatSocketManager";
import { Profile, Connected } from "../types";
import { formatName } from "../libs/formatName";
import { RemoteConfig as FirebaseRemoteConfig, fetchAndActivate, getValue } from "firebase/remote-config";

import { RootState } from "../reducers";
import { IPLocationState } from "../reducers/ipLocation";
import { TokenService } from "../libs/tokenService";
import { getCookie } from "../libs/cookies";
import RemoteConfigContext, { RemoteConfig, defaultRemoteConfig } from "../contexts/remoteConfigContext";
import ZendeskWrapper from "../libs/zendeskWrapper";

function openIntercom(profile: Profile): void {
	//currently switched off
	return;
	/**
	if (process.env.NODE_ENV === "development") {
		return;
	}

	//@ts-ignore
	window.intercomSettings = {
		app_id: process.env.REACT_APP_INTERCOM_KEY,
		name: profile.firstName + " " + profile.lastName,
		email: profile.email,
		language_override: profile.language || "en",
	};
	 **/
}

function openSatismeter(profile: Profile): void {
	if (process.env.NODE_ENV === "development") {
		return;
	}
	//@ts-ignore
	satismeter({
		writeKey: process.env.REACT_APP_SATISMETER_KEY,
		userId: profile.userId, //can be retrieved from token
		traits: {
			email: profile.email, //can be retrieved from token
			name: formatName(profile.firstName, profile.lastName), //ditto
		},
	});
}

// function windowResized() {
// 	console.log("window resized");
// 	let el = document.querySelector(".contentRight");
// 	if (el) {
// 		let w = el.clientWidth;
// 		console.log(w);
// 		let eel = el.querySelector<HTMLElement>(".oBlock");
// 		if (eel) {
// 			eel.style.width = w + "px";
// 		}
// 	}
// }

type Props = {
	history: History;
	dispatch: Dispatch;
	chatSocketManager: ChatSocketManager;
	authenticated: boolean;
	loadConversations: Connected<typeof loadConversations>;
	logout: Connected<typeof logout>;
	getPaymentCards: Connected<typeof getPaymentCards>;
	loadAdverts: Connected<typeof loadAdverts>;
	refreshMyProfile: Connected<typeof refreshMyProfile>;
	loadFriends: Connected<typeof loadFriends>;
	language?: string;
	userId?: string;
	googleAppId: string;
	googleApiKey: string;
	bodyClass: string;
	profile: Profile;
	tokenRefreshed: number;
	refreshInterval: number;
	ipLocation: IPLocationState;
	tokenService: TokenService;
	remoteConfig: FirebaseRemoteConfig;
};

const initialState = {
	newVersionAvailable: false,
	remoteWebConfig: defaultRemoteConfig,
};

type State = typeof initialState;

class App extends Component<Props, State> {
	timerId = null as number;
	remoteConfig = null as FirebaseRemoteConfig;

	constructor(props: Props) {
		super(props);
		this.remoteConfig = props.remoteConfig;

		this.state = initialState;
	}

	loadInitialData() {
		this.props.refreshMyProfile();
		this.props.loadFriends();
		this.props.getPaymentCards();
		this.props.loadAdverts();
		this.props.loadConversations();
	}

	startSocketManager() {
		const token = getAccessToken();
		this.props.chatSocketManager.start(this.props.dispatch, token);
	}

	private async refreshToken(forceRefresh: boolean = true) {
		if (
			this.props.authenticated &&
			(forceRefresh || (this.props.tokenService.shouldRefresh() && hasAuthCookies()))
		) {
			await this.props.tokenService.refresh();
		}
	}

	async componentDidMount() {
		await this.refreshToken(); //refresh token when app refreshed

		const callback = () => {
			gapi.load("auth2", () => {
				/**gapi.auth2.init({
					client_id: this.props.googleAppId,
					scope: "https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile'",
					fetch_basic_profile: false,
				}); **/
			});

			gapi.load("client", () => {
				gapi.client.init({
					clientId: this.props.googleAppId,
					apiKey: this.props.googleApiKey,
				});
			});
		};

		loadScript("https://apis.google.com/js/platform.js", "google-js")
			.then(callback)
			.catch(err => {
				console.log("error loading google platform");
				console.log({ err });
			});

		loadScript("https://accounts.google.com/gsi/client", "google-gsi");

		/*
		console.log("loading google maps api");
		await loadScript(
			`https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GMAPS_KEY}&libraries=places,geometry&language=${
				this.props.language
			}`,
			"gmapsjs"
		);
		*/

		if (this.props.authenticated && hasAuthCookies()) {
			if (this.props.history.location.pathname === "/login") {
				this.props.history.push("/");
			}

			this.loadInitialData();
			openSatismeter(this.props.profile);
			openIntercom(this.props.profile);
			this.startSocketManager();
			//this.startRefreshTimer();
		}

		fetchAndActivate(this.remoteConfig)
			.then(() => {
				this.fetchWebConfig();
			})
			.catch(err => {
				console.log("fetch and activate failed", err);
			});

		// Handle specific CSS sizing issues
		// windowResized();
		// window.addEventListener("resize", windowResized);
	}

	//periodically refresh token in background
	startRefreshTimer = () => {
		this.timerId = window.setInterval(() => {
			this.fetchWebConfig();
			this.props.tokenService.refresh().catch(err => {
				this.stopRefreshTimer();
				this.props.logout();
			});
		}, this.props.refreshInterval - 10000);
	};

	fetchWebConfig = () => {
		const webConfig = JSON.parse(getValue(this.remoteConfig, "playerWebAppConfig").asString()) as RemoteConfig;

		let newVersionAvailable = false;
		if (webConfig.latestVersion !== process.env.REACT_APP_VERSION) {
			newVersionAvailable = true;
		}

		if (webConfig.brandedLogo) {
			const countryCode = getCookie("countryCode") || this.props.profile.country;
			if (countryCode) {
				document.body.dataset.country = countryCode.toLowerCase();
			}
		}

		this.setState({ remoteWebConfig: webConfig, newVersionAvailable });
	};

	stopRefreshTimer = () => {
		window.clearInterval(this.timerId);
	};

	async componentDidUpdate(prevProps: Props, prevState) {
		await this.refreshToken(false); //refresh token on page changes

		if (!this.props.authenticated) {
			this.props.chatSocketManager.stop();
			this.stopRefreshTimer();
			removeTokens();
		}

		if (this.props.authenticated && !prevProps.authenticated) {
			this.loadInitialData();
			this.startSocketManager();
			openSatismeter(this.props.profile);
			this.startRefreshTimer();
		}

		if (this.props.authenticated && prevProps.tokenRefreshed !== this.props.tokenRefreshed) {
			this.props.chatSocketManager.stop();
			this.startSocketManager();
		}
	}

	componentWillUnmount() {
		this.stopRefreshTimer();
		this.props.chatSocketManager.stop();
	}

	render() {
		const userContext = {
			language: this.props.language,
			userId: this.props.userId,
			profile: this.props.profile,
			location: this.props.ipLocation,
		};

		const layoutContext = {
			authenticated: this.props.authenticated,
			logout: this.props.logout,
			history: this.props.history,
			profile: this.props.profile,
			newVersionAvailable: this.state.newVersionAvailable,
			bodyClass: this.props.bodyClass,
		} as LayoutContextProps;

		return (
			<>
				<ConnectedRouter history={this.props.history}>
					<UserContext.Provider value={userContext}>
						<ChatSocketContext.Provider value={this.props.chatSocketManager}>
							<LayoutContext.Provider value={layoutContext}>
								<RemoteConfigContext.Provider value={this.state.remoteWebConfig}>
									<Routes />
									<ZendeskWrapper />
								</RemoteConfigContext.Provider>
							</LayoutContext.Provider>
						</ChatSocketContext.Provider>
					</UserContext.Provider>
				</ConnectedRouter>
			</>
		);
	}
}

function mapStateToProps({ auth, profile, router, ipLocation }: RootState) {
	const bodyClass = bodyClassSelector(router.location.pathname);
	return { ...auth, profile, ipLocation, bodyClass };
}

export default withRedux(
	{ loadConversations, logout, getPaymentCards, loadAdverts, refreshMyProfile, loadFriends },
	mapStateToProps
)(App);
